import {FormEvent, useRef, useState} from 'react'
import {Building2, Camera, Mail, Save, ShieldCheck, UserRound} from 'lucide-react'

import {toAbsoluteUrl} from '../../../_metronic/helpers'
import * as API from '../../api'
import CropperComponent from '../../pages/utils/ImageCropper'
import {getUserData} from '../auth'
import {isAdmin, isSuperAdmin} from '../auth/core/Auth'

type Props = {
  onUpdated: (profile: any) => void
}

const ProfileDataPage = ({onUpdated}: Props) => {
  const user = getUserData()
  const inputRef = useRef<HTMLInputElement>(null)
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [croppedImage, setCroppedImage] = useState('')
  const [uploadResponse, setUploadResponse] = useState<any>()
  const [showCropper, setShowCropper] = useState(false)
  const [loading, setLoading] = useState(false)
  const [name, setName] = useState(user?.name || '')
  const [savedName, setSavedName] = useState(user?.name || '')
  const [feedback, setFeedback] = useState('')
  const [feedbackType, setFeedbackType] = useState<'success' | 'error'>('success')

  const validationMessage =
    name.trim().length < 5 ? 'O nome deve conter no mínimo 5 caracteres.' : ''
  const hasChanges = name.trim() !== savedName || Boolean(uploadResponse)
  const avatar =
    croppedImage || user?.src || toAbsoluteUrl('/media/icons/duotune/general/gen049.svg')
  const role = isSuperAdmin() ? 'Super administrador' : isAdmin() ? 'Administrador' : 'Membro'

  function selectImage(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0]
    if (!file) return
    setImageFile(file)
    setShowCropper(true)
    setFeedback('')
    const gtag = (window as any).gtag
    gtag?.('event', 'click', {
      event_category: 'Dados Pessoais',
      event_label: 'Alterar Imagem',
    })
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault()
    if (validationMessage || !hasChanges || loading) return
    setLoading(true)
    setFeedback('')
    const gtag = (window as any).gtag
    gtag?.('event', 'submit', {
      event_category: 'Dados Pessoais',
      event_label: 'Atualizar dados',
    })

    try {
      const response = await API.updateUser({
        name: name.trim(),
        user_guid: user.guid,
        email: user.email,
        password: '',
        is_actived: user.is_actived ?? 1,
        id_upload: uploadResponse?.id_upload,
        is_admin: user.is_admin,
        is_mailling: user.is_mailling,
        is_viewer: user.is_viewer,
        id_seller: user.id_seller,
        company_guid: user.company_guid,
      })
      if (response?.success === false) throw new Error('Profile update failed')

      const refreshed = await API.updateUserData()
      const updatedProfile = {
        ...user,
        ...(refreshed?.data || {}),
        name: name.trim(),
      }
      localStorage.setItem('auth', JSON.stringify(updatedProfile))
      onUpdated(updatedProfile)
      setSavedName(name.trim())
      setUploadResponse(undefined)
      setFeedbackType('success')
      setFeedback('Dados pessoais atualizados com sucesso.')
    } catch {
      setFeedbackType('error')
      setFeedback('Não foi possível atualizar seus dados. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <form className='profile-form' onSubmit={handleSubmit}>
      <div className='profile-form__body'>
        <section className='profile-form__heading'>
          <span>
            <UserRound size={18} />
          </span>
          <div>
            <h3>Dados pessoais</h3>
            <p>Mantenha seu nome e sua imagem de identificação atualizados.</p>
          </div>
        </section>

        <div className='profile-personal__grid'>
          <section className='profile-photo-card'>
            <span className='profile-photo-card__avatar'>
              <img src={avatar} alt='Foto do perfil' />
            </span>
            <strong>{savedName}</strong>
            <small>{role}</small>
            <input
              ref={inputRef}
              type='file'
              accept='image/png,image/jpeg'
              hidden
              onChange={selectImage}
            />
            <button
              type='button'
              className='ua-secondary-button'
              onClick={() => inputRef.current?.click()}
            >
              <Camera size={14} /> Trocar foto
            </button>
            <span className='profile-photo-card__hint'>
              JPG ou PNG, com enquadramento quadrado.
            </span>
          </section>

          <section className='profile-fields-card'>
            <div className='profile-field'>
              <label htmlFor='profile-name'>
                <UserRound size={13} /> Nome completo
              </label>
              <input
                id='profile-name'
                value={name}
                className={validationMessage ? 'is-invalid' : hasChanges ? 'is-valid' : ''}
                autoComplete='name'
                placeholder='Seu nome completo'
                onChange={(event) => {
                  setName(event.target.value)
                  setFeedback('')
                }}
              />
              {validationMessage && <small>{validationMessage}</small>}
            </div>

            <div className='profile-field'>
              <label htmlFor='profile-email'>
                <Mail size={13} /> E-mail de acesso
              </label>
              <input id='profile-email' value={user?.email || ''} disabled readOnly />
              <small className='is-neutral'>
                O e-mail de acesso não pode ser alterado nesta tela.
              </small>
            </div>

            <div className='profile-account-details'>
              <span>
                <Building2 size={14} />
                <span>
                  <small>Empresa</small>
                  <strong>{user?.company_name || 'Não informada'}</strong>
                </span>
              </span>
              <span>
                <ShieldCheck size={14} />
                <span>
                  <small>Perfil de acesso</small>
                  <strong>{role}</strong>
                </span>
              </span>
            </div>
          </section>
        </div>

        {showCropper && imageFile && (
          <section className='profile-cropper-card'>
            <div>
              <h3>Ajustar nova foto</h3>
              <p>Posicione a imagem dentro do recorte antes de confirmar.</p>
            </div>
            <CropperComponent
              imageFile={imageFile}
              setCroppedImage={setCroppedImage}
              setShowCropper={setShowCropper}
              setUploadResponse={setUploadResponse}
            />
          </section>
        )}

        {feedback && (
          <div
            className={`profile-feedback ${
              feedbackType === 'error' ? 'profile-feedback--danger' : ''
            }`}
            role='status'
          >
            {feedback}
          </div>
        )}
      </div>

      <footer className='profile-form__footer'>
        <span>
          {hasChanges ? 'Existem alterações ainda não salvas.' : 'Seus dados estão atualizados.'}
        </span>
        <button
          type='submit'
          className='ua-primary-button'
          disabled={Boolean(validationMessage || !hasChanges || loading || showCropper)}
        >
          {loading ? <span className='ua-spinner' /> : <Save size={14} />}
          Salvar alterações
        </button>
      </footer>
    </form>
  )
}

export default ProfileDataPage
